import type { Point, Portal, Segment } from "../types";

const FULL_TURN = Math.PI * 2;
const normalizeAngle = (angle: number) => ((angle % FULL_TURN) + FULL_TURN) % FULL_TURN;

export const distance = (a: Point, b: Point) => Math.hypot(a.x - b.x, a.y - b.y);

export function nearestPointOnSegment(point: Point, segment: Segment): Point {
  const dx = segment.b.x - segment.a.x;
  const dy = segment.b.y - segment.a.y;
  const lengthSquared = dx * dx + dy * dy;
  if (!lengthSquared) return segment.a;
  const t = Math.max(0, Math.min(1, ((point.x - segment.a.x) * dx + (point.y - segment.a.y) * dy) / lengthSquared));
  return { x: segment.a.x + t * dx, y: segment.a.y + t * dy };
}

export function raySegmentIntersection(origin: Point, angle: number, segment: Segment, radius: number): Point | null {
  const r = { x: Math.cos(angle), y: Math.sin(angle) };
  const s = { x: segment.b.x - segment.a.x, y: segment.b.y - segment.a.y };
  const cross = r.x * s.y - r.y * s.x;
  if (Math.abs(cross) < 1e-9) return null;
  const q = { x: segment.a.x - origin.x, y: segment.a.y - origin.y };
  const t = (q.x * s.y - q.y * s.x) / cross;
  const u = (q.x * r.y - q.y * r.x) / cross;
  if (t < 0 || t > radius || u < 0 || u > 1) return null;
  return { x: origin.x + t * r.x, y: origin.y + t * r.y };
}

export function visibilityPolygon(origin: Point, radius: number, segments: Segment[], sampleCount = 180): Point[] {
  const nearby = segments.filter((segment) => {
    const minX = Math.min(segment.a.x, segment.b.x) - radius;
    const maxX = Math.max(segment.a.x, segment.b.x) + radius;
    const minY = Math.min(segment.a.y, segment.b.y) - radius;
    const maxY = Math.max(segment.a.y, segment.b.y) + radius;
    return origin.x >= minX && origin.x <= maxX && origin.y >= minY && origin.y <= maxY;
  });
  const angles = new Set<number>();
  for (let index = 0; index < sampleCount; index += 1) angles.add((index / sampleCount) * FULL_TURN);
  for (const segment of nearby) {
    for (const endpoint of [segment.a, segment.b]) {
      const angle = Math.atan2(endpoint.y - origin.y, endpoint.x - origin.x);
      angles.add(normalizeAngle(angle - 0.0001));
      angles.add(normalizeAngle(angle));
      angles.add(normalizeAngle(angle + 0.0001));
    }
  }
  return [...angles].sort((a, b) => a - b).map((angle) => {
    let closest: Point = { x: origin.x + Math.cos(angle) * radius, y: origin.y + Math.sin(angle) * radius };
    let closestDistance = radius;
    for (const segment of nearby) {
      const hit = raySegmentIntersection(origin, angle, segment, radius);
      if (!hit) continue;
      const hitDistance = distance(origin, hit);
      if (hitDistance < closestDistance) { closest = hit; closestDistance = hitDistance; }
    }
    return closest;
  });
}

export const blockingSegments = (walls: Segment[], portals: Portal[]) => [
  ...walls,
  ...portals.filter((portal) => portal.state !== "open" && (portal.blocksSight ?? portal.kind !== "window")),
];

/** Adds the map edge to walls and closed portals for sight and light casting. */
export function boundedBlockingSegments(bounds: { width: number; height: number }, walls: Segment[], portals: Portal[]): Segment[] {
  return [
    ...blockingSegments(walls, portals),
    { id: "bound-top", a: { x: 0, y: 0 }, b: { x: bounds.width, y: 0 } },
    { id: "bound-right", a: { x: bounds.width, y: 0 }, b: { x: bounds.width, y: bounds.height } },
    { id: "bound-bottom", a: { x: bounds.width, y: bounds.height }, b: { x: 0, y: bounds.height } },
    { id: "bound-left", a: { x: 0, y: bounds.height }, b: { x: 0, y: 0 } },
  ];
}

export function pointInPolygon(point: Point, polygon: Point[]): boolean {
  let inside = false;
  for (let i = 0, j = polygon.length - 1; i < polygon.length; j = i++) {
    const a = polygon[i]; const b = polygon[j];
    const intersect = (a.y > point.y) !== (b.y > point.y) && point.x < ((b.x - a.x) * (point.y - a.y)) / (b.y - a.y || 1e-9) + a.x;
    if (intersect) inside = !inside;
  }
  return inside;
}

const orientation = (a: Point, b: Point, c: Point) => (b.x - a.x) * (c.y - a.y) - (b.y - a.y) * (c.x - a.x);
const pointOnClosedSegment = (point: Point, a: Point, b: Point) => Math.abs(orientation(a, b, point)) <= 1e-8
  && point.x >= Math.min(a.x, b.x) - 1e-8 && point.x <= Math.max(a.x, b.x) + 1e-8
  && point.y >= Math.min(a.y, b.y) - 1e-8 && point.y <= Math.max(a.y, b.y) + 1e-8;

const closedSegmentsIntersect = (a: Point, b: Point, c: Point, d: Point) => {
  const abC = orientation(a, b, c); const abD = orientation(a, b, d); const cdA = orientation(c, d, a); const cdB = orientation(c, d, b);
  if (((abC > 0 && abD < 0) || (abC < 0 && abD > 0)) && ((cdA > 0 && cdB < 0) || (cdA < 0 && cdB > 0))) return true;
  return pointOnClosedSegment(c, a, b) || pointOnClosedSegment(d, a, b) || pointOnClosedSegment(a, c, d) || pointOnClosedSegment(b, c, d);
};

export function polygonsIntersect(a: Point[], b: Point[]): boolean {
  if (a.length < 3 || b.length < 3) return false;
  const bounds = (polygon: Point[]) => ({ minX: Math.min(...polygon.map((point) => point.x)), maxX: Math.max(...polygon.map((point) => point.x)), minY: Math.min(...polygon.map((point) => point.y)), maxY: Math.max(...polygon.map((point) => point.y)) });
  const aBounds = bounds(a); const bBounds = bounds(b);
  if (aBounds.maxX < bBounds.minX || bBounds.maxX < aBounds.minX || aBounds.maxY < bBounds.minY || bBounds.maxY < aBounds.minY) return false;
  if (pointInPolygon(a[0], b) || pointInPolygon(b[0], a)) return true;
  for (let aIndex = 0; aIndex < a.length; aIndex += 1) {
    const aNext = (aIndex + 1) % a.length;
    for (let bIndex = 0; bIndex < b.length; bIndex += 1) {
      if (closedSegmentsIntersect(a[aIndex], a[aNext], b[bIndex], b[(bIndex + 1) % b.length])) return true;
    }
  }
  return false;
}

export function clampPointToBounds(point: Point, bounds: { width: number; height: number }): Point {
  return {
    x: Math.max(0, Math.min(Math.max(0, bounds.width), Number.isFinite(point.x) ? point.x : 0)),
    y: Math.max(0, Math.min(Math.max(0, bounds.height), Number.isFinite(point.y) ? point.y : 0)),
  };
}

export function pointInBounds(point: Point, bounds: { width: number; height: number }): boolean {
  return Number.isFinite(point.x) && Number.isFinite(point.y) && point.x >= 0 && point.y >= 0 && point.x <= bounds.width && point.y <= bounds.height;
}

/** Clips a painted polygon to the map rectangle so it cannot spill into letterboxing. */
export function clipPolygonToBounds(polygon: Point[], bounds: { width: number; height: number }): Point[] {
  if (polygon.length < 3 || !Number.isFinite(bounds.width) || !Number.isFinite(bounds.height) || bounds.width <= 0 || bounds.height <= 0) return [];
  type Edge = { inside: (point: Point) => boolean; intersection: (from: Point, to: Point) => Point };
  const verticalIntersection = (x: number, from: Point, to: Point): Point => {
    const span = to.x - from.x; const ratio = Math.abs(span) < 1e-9 ? 0 : (x - from.x) / span;
    return { x, y: from.y + (to.y - from.y) * ratio };
  };
  const horizontalIntersection = (y: number, from: Point, to: Point): Point => {
    const span = to.y - from.y; const ratio = Math.abs(span) < 1e-9 ? 0 : (y - from.y) / span;
    return { x: from.x + (to.x - from.x) * ratio, y };
  };
  const edges: Edge[] = [
    { inside: (point) => point.x >= 0, intersection: (from, to) => verticalIntersection(0, from, to) },
    { inside: (point) => point.x <= bounds.width, intersection: (from, to) => verticalIntersection(bounds.width, from, to) },
    { inside: (point) => point.y >= 0, intersection: (from, to) => horizontalIntersection(0, from, to) },
    { inside: (point) => point.y <= bounds.height, intersection: (from, to) => horizontalIntersection(bounds.height, from, to) },
  ];
  let output = polygon.filter((point) => Number.isFinite(point.x) && Number.isFinite(point.y));
  for (const edge of edges) {
    const input = output; output = [];
    if (!input.length) break;
    let previous = input.at(-1)!; let previousInside = edge.inside(previous);
    for (const current of input) {
      const currentInside = edge.inside(current);
      if (currentInside !== previousInside) output.push(edge.intersection(previous, current));
      if (currentInside) output.push(current);
      previous = current; previousInside = currentInside;
    }
  }
  const cleaned: Point[] = [];
  for (const point of output.map((item) => clampPointToBounds(item, bounds))) {
    if (!cleaned.length || distance(cleaned.at(-1)!, point) > 1e-7) cleaned.push(point);
  }
  if (cleaned.length > 1 && distance(cleaned[0], cleaned.at(-1)!) <= 1e-7) cleaned.pop();
  return cleaned.length >= 3 ? cleaned : [];
}

/** Builds one rounded polygon around a freehand center line in grid-square units. */
export function brushStrokePolygon(input: Point[], radius: number, capSteps = 10): Point[] {
  const safeRadius = Math.max(0.05, radius);
  const points = input.filter((point, index) => index === 0 || distance(point, input[index - 1]) > 0.0001);
  if (!points.length) return [];
  if (points.length === 1) return Array.from({ length: Math.max(8, capSteps * 2) }, (_, index) => {
    const angle = (index / Math.max(8, capSteps * 2)) * FULL_TURN;
    return { x: points[0].x + Math.cos(angle) * safeRadius, y: points[0].y + Math.sin(angle) * safeRadius };
  });
  const normals = points.map((point, index) => {
    const previous = points[Math.max(0, index - 1)]; const next = points[Math.min(points.length - 1, index + 1)];
    const dx = next.x - previous.x; const dy = next.y - previous.y; const length = Math.hypot(dx, dy) || 1;
    return { x: -dy / length, y: dx / length };
  });
  const left = points.map((point, index) => ({ x: point.x + normals[index].x * safeRadius, y: point.y + normals[index].y * safeRadius }));
  const right = points.map((point, index) => ({ x: point.x - normals[index].x * safeRadius, y: point.y - normals[index].y * safeRadius }));
  const last = points.length - 1; const endLeftAngle = Math.atan2(normals[last].y, normals[last].x); const startRightAngle = Math.atan2(-normals[0].y, -normals[0].x);
  const endCap = Array.from({ length: capSteps }, (_, index) => { const angle = endLeftAngle - (Math.PI * (index + 1)) / capSteps; return { x: points[last].x + Math.cos(angle) * safeRadius, y: points[last].y + Math.sin(angle) * safeRadius }; });
  const startCap = Array.from({ length: capSteps }, (_, index) => { const angle = startRightAngle - (Math.PI * (index + 1)) / capSteps; return { x: points[0].x + Math.cos(angle) * safeRadius, y: points[0].y + Math.sin(angle) * safeRadius }; });
  return [...left, ...endCap, ...right.reverse(), ...startCap];
}

const pointSegmentDistance = (point: Point, a: Point, b: Point) => distance(point, nearestPointOnSegment(point, { id: "brush-segment", a, b }));

function simplifyOpenPath(points: Point[], tolerance: number): Point[] {
  if (points.length <= 2) return points;
  let farthest = 0; let farthestDistance = 0;
  for (let index = 1; index < points.length - 1; index += 1) {
    const candidate = pointSegmentDistance(points[index], points[0], points.at(-1)!);
    if (candidate > farthestDistance) { farthest = index; farthestDistance = candidate; }
  }
  if (farthestDistance <= tolerance) return [points[0], points.at(-1)!];
  return [...simplifyOpenPath(points.slice(0, farthest + 1), tolerance).slice(0, -1), ...simplifyOpenPath(points.slice(farthest), tolerance)];
}

function simplifyClosedPath(points: Point[], tolerance: number): Point[] {
  if (points.length <= 8) return points;
  let opposite = 1; let farthest = 0;
  for (let index = 1; index < points.length; index += 1) {
    const candidate = distance(points[0], points[index]);
    if (candidate > farthest) { farthest = candidate; opposite = index; }
  }
  const first = simplifyOpenPath(points.slice(0, opposite + 1), tolerance);
  const second = simplifyOpenPath([...points.slice(opposite), points[0]], tolerance);
  const joined = [...first.slice(0, -1), ...second.slice(0, -1)];
  if (joined.length <= 512) return joined;
  const stride = Math.ceil(joined.length / 512); return joined.filter((_, index) => index % stride === 0);
}

/**
 * Turns a freehand brush gesture into the clean perimeter of its painted area.
 * A small occupancy grid unions overlapping brush capsules, fills scribbled
 * interiors, and then traces/simplifies only the outside boundary.
 */
export function brushAreaPolygon(input: Point[], radius: number): Point[] {
  const safeRadius = Math.max(0.05, radius);
  const points = input.filter((point, index) => Number.isFinite(point.x) && Number.isFinite(point.y) && (index === 0 || distance(point, input[index - 1]) > 0.0001));
  if (points.length <= 1) return brushStrokePolygon(points, safeRadius);
  const minPointX = Math.min(...points.map((point) => point.x)) - safeRadius; const maxPointX = Math.max(...points.map((point) => point.x)) + safeRadius;
  const minPointY = Math.min(...points.map((point) => point.y)) - safeRadius; const maxPointY = Math.max(...points.map((point) => point.y)) + safeRadius;
  const desiredCell = Math.max(0.04, Math.min(0.16, safeRadius / 4));
  const cell = Math.max(desiredCell, (maxPointX - minPointX) / 480, (maxPointY - minPointY) / 480);
  const originX = minPointX - cell; const originY = minPointY - cell;
  const columns = Math.max(3, Math.ceil((maxPointX - minPointX) / cell) + 2); const rows = Math.max(3, Math.ceil((maxPointY - minPointY) / cell) + 2);
  const occupied = new Uint8Array(columns * rows); const indexOf = (x: number, y: number) => y * columns + x;
  for (let segmentIndex = 1; segmentIndex < points.length; segmentIndex += 1) {
    const a = points[segmentIndex - 1]; const b = points[segmentIndex];
    const fromX = Math.max(0, Math.floor((Math.min(a.x, b.x) - safeRadius - originX) / cell)); const toX = Math.min(columns - 1, Math.ceil((Math.max(a.x, b.x) + safeRadius - originX) / cell));
    const fromY = Math.max(0, Math.floor((Math.min(a.y, b.y) - safeRadius - originY) / cell)); const toY = Math.min(rows - 1, Math.ceil((Math.max(a.y, b.y) + safeRadius - originY) / cell));
    for (let y = fromY; y <= toY; y += 1) for (let x = fromX; x <= toX; x += 1) {
      const sample = { x: originX + (x + 0.5) * cell, y: originY + (y + 0.5) * cell };
      if (pointSegmentDistance(sample, a, b) <= safeRadius + cell * 0.35) occupied[indexOf(x, y)] = 1;
    }
  }
  type GridPoint = { x: number; y: number }; type GridEdge = { from: GridPoint; to: GridPoint };
  const outgoing = new Map<string, GridEdge[]>(); const key = (point: GridPoint) => `${point.x},${point.y}`;
  const addEdge = (from: GridPoint, to: GridPoint) => { const entry = outgoing.get(key(from)) ?? []; entry.push({ from, to }); outgoing.set(key(from), entry); };
  const filled = (x: number, y: number) => x >= 0 && y >= 0 && x < columns && y < rows && occupied[indexOf(x, y)] === 1;
  for (let y = 0; y < rows; y += 1) for (let x = 0; x < columns; x += 1) {
    if (!filled(x, y)) continue;
    if (!filled(x, y - 1)) addEdge({ x, y }, { x: x + 1, y });
    if (!filled(x + 1, y)) addEdge({ x: x + 1, y }, { x: x + 1, y: y + 1 });
    if (!filled(x, y + 1)) addEdge({ x: x + 1, y: y + 1 }, { x, y: y + 1 });
    if (!filled(x - 1, y)) addEdge({ x, y: y + 1 }, { x, y });
  }
  const loops: GridPoint[][] = [];
  while (outgoing.size) {
    const firstEntry = outgoing.entries().next().value as [string, GridEdge[]] | undefined; if (!firstEntry) break;
    const startEdge = firstEntry[1].pop()!; if (!firstEntry[1].length) outgoing.delete(firstEntry[0]);
    const loop = [startEdge.from]; let current = startEdge.to; let guard = 0;
    while (key(current) !== key(loop[0]) && guard < columns * rows * 4) {
      loop.push(current); const entryKey = key(current); const choices = outgoing.get(entryKey); if (!choices?.length) break;
      const next = choices.pop()!; if (!choices.length) outgoing.delete(entryKey); current = next.to; guard += 1;
    }
    if (loop.length >= 3 && key(current) === key(loop[0])) loops.push(loop);
  }
  const signedArea = (polygon: GridPoint[]) => polygon.reduce((sum, point, index) => { const next = polygon[(index + 1) % polygon.length]; return sum + point.x * next.y - next.x * point.y; }, 0) / 2;
  const boundary = loops.sort((a, b) => Math.abs(signedArea(b)) - Math.abs(signedArea(a)))[0];
  if (!boundary) return brushStrokePolygon(points, safeRadius);
  const world = boundary.map((point) => ({ x: originX + point.x * cell, y: originY + point.y * cell }));
  return simplifyClosedPath(world, cell * 1.35);
}

/** Adds a freehand sample while keeping long painting sessions bounded. */
export function appendBrushStrokePoint(input: Point[], point: Point, minimumDistance: number, maxPoints = 512): Point[] {
  const last = input.at(-1);
  if (last && distance(last, point) < minimumDistance) return input;
  const appended = [...input, point];
  if (appended.length <= Math.max(8, maxPoints)) return appended;
  return appended.filter((_, index) => index % 2 === 0 || index === appended.length - 1);
}
